// Pure helpers for rendering action receipts. A receipt is the server's record
// of one change to one physical copy: {id, kind, entry_id, summary, before,
// after, created_at, undoable, undone_at}.

// The server records full rows in before/after. Only the fields a person edits
// or would recognise are shown; revision, timestamps and cached price columns
// are noise, not changes the user made.
const FIELD_ORDER = ['printing', 'variant_label', 'foil_pattern', 'variant_id', 'distribution', 'condition', 'favorite', 'notes', 'is_trade', 'list_type', 'location_id', 'compartment_id', 'position', 'language', 'purchase_price'];

function show(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number' && (v === 0 || v === 1)) return v === 1 ? 'yes' : 'no';
  return String(v);
}

// Which allowlisted fields actually changed, in a stable, human order. Fields
// present in only one side count as changed (a variant label cleared by a
// finish edit).
export function diffLines(receipt) {
  const before = receipt?.before && typeof receipt.before === 'object' ? receipt.before : {};
  const after = receipt?.after && typeof receipt.after === 'object' ? receipt.after : {};
  return FIELD_ORDER
    .filter(k => (k in before || k in after) && String(before[k] ?? '') !== String(after[k] ?? ''))
    .map(k => ({ field: k, from: show(before[k]), to: show(after[k]) }));
}

export function undoState(receipt) {
  if (!receipt) return 'locked';
  if (receipt.undone_at) return 'undone';
  return receipt.undoable ? 'undoable' : 'locked';
}

// "just now", "4m", "2h", or a short date. Takes `now` so tests are stable.
export function receiptAge(created_at, now = Date.now()) {
  const t = Date.parse(created_at);
  if (!Number.isFinite(t) || t < Date.UTC(2000, 0, 1)) return ''; // epoch-era sentinels are unknown, never shown
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = new Date(t);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Newest first, with undone receipts kept (they are part of the story) but
// after live ones of the same age is not needed: creation time is enough.
export function sortReceipts(receipts) {
  return [...(receipts || [])].sort((a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0));
}
